import pick from 'lodash/pick';
import {
  closeListingAfterSold,
  initiatePrivileged,
  sendGuestBuyerOrderConfirmation,
  transitionPrivileged,
} from '../../util/api';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { isTicketNetworkEventListing } from '../../util/listingEventDate';
import * as log from '../../util/log';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUserHasOrdersSuccess, fetchCurrentUser } from '../../ducks/user.duck';

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/CheckoutPage/SET_INITIAL_VALUES';

export const INITIATE_ORDER_REQUEST = 'app/CheckoutPage/INITIATE_ORDER_REQUEST';
export const INITIATE_ORDER_SUCCESS = 'app/CheckoutPage/INITIATE_ORDER_SUCCESS';
export const INITIATE_ORDER_ERROR = 'app/CheckoutPage/INITIATE_ORDER_ERROR';

export const CONFIRM_PAYMENT_REQUEST = 'app/CheckoutPage/CONFIRM_PAYMENT_REQUEST';
export const CONFIRM_PAYMENT_SUCCESS = 'app/CheckoutPage/CONFIRM_PAYMENT_SUCCESS';
export const CONFIRM_PAYMENT_ERROR = 'app/CheckoutPage/CONFIRM_PAYMENT_ERROR';

export const SPECULATE_TRANSACTION_REQUEST = 'app/CheckoutPage/SPECULATE_TRANSACTION_REQUEST';
export const SPECULATE_TRANSACTION_SUCCESS = 'app/CheckoutPage/SPECULATE_TRANSACTION_SUCCESS';
export const SPECULATE_TRANSACTION_ERROR = 'app/CheckoutPage/SPECULATE_TRANSACTION_ERROR';

export const STRIPE_CUSTOMER_REQUEST = 'app/CheckoutPage/STRIPE_CUSTOMER_REQUEST';
export const STRIPE_CUSTOMER_SUCCESS = 'app/CheckoutPage/STRIPE_CUSTOMER_SUCCESS';
export const STRIPE_CUSTOMER_ERROR = 'app/CheckoutPage/STRIPE_CUSTOMER_ERROR';

// ================ Reducer ================ //

const initialState = {
  listing: null,
  orderData: null,
  speculateTransactionInProgress: false,
  speculateTransactionError: null,
  speculatedTransaction: null,
  transaction: null,
  initiateOrderError: null,
  confirmPaymentError: null,
  stripeCustomerFetched: false,
};

export default function checkoutPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SPECULATE_TRANSACTION_REQUEST:
      return {
        ...state,
        speculateTransactionInProgress: true,
        speculateTransactionError: null,
        // Keep the last speculated transaction so checkout (incl. TEAK #tg-placeholder)
        // stays mounted during re-speculation (e.g. opt-in/out); avoids full-page flash.
        speculatedTransaction: state.speculatedTransaction,
      };
    case SPECULATE_TRANSACTION_SUCCESS:
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculatedTransaction: payload.transaction,
      };
    case SPECULATE_TRANSACTION_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculateTransactionError: payload,
      };

    case INITIATE_ORDER_REQUEST:
      return { ...state, initiateOrderError: null };
    case INITIATE_ORDER_SUCCESS:
      return { ...state, transaction: payload };
    case INITIATE_ORDER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, initiateOrderError: payload };

    case CONFIRM_PAYMENT_REQUEST:
      return { ...state, confirmPaymentError: null };
    case CONFIRM_PAYMENT_SUCCESS:
      return state;
    case CONFIRM_PAYMENT_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, confirmPaymentError: payload };

    case STRIPE_CUSTOMER_REQUEST:
      return { ...state, stripeCustomerFetched: false };
    case STRIPE_CUSTOMER_SUCCESS:
      return { ...state, stripeCustomerFetched: true };
    case STRIPE_CUSTOMER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, stripeCustomerFetchError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const initiateOrderRequest = () => ({ type: INITIATE_ORDER_REQUEST });

const initiateOrderSuccess = order => ({
  type: INITIATE_ORDER_SUCCESS,
  payload: order,
});

const initiateOrderError = e => ({
  type: INITIATE_ORDER_ERROR,
  error: true,
  payload: e,
});

const confirmPaymentRequest = () => ({ type: CONFIRM_PAYMENT_REQUEST });

const confirmPaymentSuccess = orderId => ({
  type: CONFIRM_PAYMENT_SUCCESS,
  payload: orderId,
});

const confirmPaymentError = e => ({
  type: CONFIRM_PAYMENT_ERROR,
  error: true,
  payload: e,
});

export const speculateTransactionRequest = () => ({ type: SPECULATE_TRANSACTION_REQUEST });

export const speculateTransactionSuccess = transaction => ({
  type: SPECULATE_TRANSACTION_SUCCESS,
  payload: { transaction },
});

export const speculateTransactionError = e => ({
  type: SPECULATE_TRANSACTION_ERROR,
  error: true,
  payload: e,
});

export const stripeCustomerRequest = () => ({ type: STRIPE_CUSTOMER_REQUEST });
export const stripeCustomerSuccess = () => ({ type: STRIPE_CUSTOMER_SUCCESS });
export const stripeCustomerError = e => ({
  type: STRIPE_CUSTOMER_ERROR,
  error: true,
  payload: e,
});

/* ================ Thunks ================ */

/**
 * Load listing images (and card variants) for checkout when session/Redux listing
 * was stored without included images (common on direct checkout refresh).
 */
export const fetchCheckoutListingMedia = (listingId, config) => (dispatch, getState, sdk) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const params = {
    id: listingId,
    include: ['images'],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',
      `variants.${variantPrefix}-thumb`,
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,
      'variants.facebook',
      'variants.twitter',
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}-thumb`, 200, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  return sdk.listings
    .show(params)
    .then(data => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(data, sanitizeConfig));
      const listings = denormalisedResponseEntities(data);
      return listings[0] || null;
    })
    .catch(e => {
      log.error(e, 'fetch-checkout-listing-media-failed');
      return null;
    });
};

export const initiateOrder = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => (dispatch, getState, sdk) => {
  dispatch(initiateOrderRequest());

  // If we already have a transaction ID, we should transition, not
  // initiate.
  const isTransition = !!transactionId;

  const {
    deliveryMethod,
    quantity,
    stockReservationQuantity,
    bookingDates,
    teakPremiumSubUnits,
    rewardsRedeemSubUnits,
    tnSelection,
    ...otherOrderParams
  } = orderParams;
  // Resolve stock qty from every checkout alias. Staging rewards initiates were arriving
  // with rewards/TEAK/paymentMethod but no quantity — recover from protectedData.tnSelection too.
  const resolvedQuantity = (() => {
    const raw =
      quantity != null
        ? quantity
        : stockReservationQuantity != null
        ? stockReservationQuantity
        : tnSelection?.quantity != null
        ? tnSelection.quantity
        : otherOrderParams?.protectedData?.tnSelection?.quantity;
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
      return Math.round(raw);
    }
    if (raw != null && typeof raw === 'object' && typeof raw.toNumber === 'function') {
      const n = raw.toNumber();
      return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
    }
    const n = typeof raw === 'number' ? raw : Number.parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  const quantityMaybe = resolvedQuantity ? { stockReservationQuantity: resolvedQuantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  // stockReservationQuantity on orderData too — lineItems.js reads the merged object.
  // tnSelection stays on orderData only (never forward to Sharetribe transition params).
  const orderData = {
    ...(deliveryMethod ? { deliveryMethod } : {}),
    ...(teakPremiumSubUnits != null ? { teakPremiumSubUnits } : {}),
    ...(rewardsRedeemSubUnits != null ? { rewardsRedeemSubUnits } : {}),
    ...quantityMaybe,
    ...(tnSelection ? { tnSelection } : {}),
  };

  // Parameters for Marketplace API
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
  };

  // Privileged initiate strips top-level tnSelection (Sharetribe rejects unknown params).
  // Copy ticketGroupId onto protectedData so the TN poller can fulfill event listings.
  if (tnSelection && typeof tnSelection === 'object') {
    const pd = { ...(transitionParams.protectedData || {}) };
    const existing = pd.tnSelection && typeof pd.tnSelection === 'object' ? pd.tnSelection : {};
    const ticketGroupId = String(
      existing.ticketGroupId || tnSelection.ticketGroupId || tnSelection.tgId || tnSelection.tgID || ''
    ).trim();
    const eventId = String(existing.eventId || tnSelection.eventId || '').trim();
    pd.tnSelection = {
      ...existing,
      ...(ticketGroupId ? { ticketGroupId } : {}),
      ...(eventId ? { eventId } : {}),
    };
    transitionParams.protectedData = pd;
  }

  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition: transitionName,
        params: transitionParams,
      }
    : {
        processAlias,
        transition: transitionName,
        params: transitionParams,
      };
  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSucces = response => {
    const entities = denormalisedResponseEntities(response);
    const order = entities[0];
    dispatch(initiateOrderSuccess(order));
    dispatch(fetchCurrentUserHasOrdersSuccess(true));
    return order;
  };

  const handleError = e => {
    dispatch(initiateOrderError(storableError(e)));
    const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
    log.error(e, 'initiate-order-failed', {
      ...transactionIdMaybe,
      listingId: orderParams.listingId.uuid,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
    });
    throw e;
  };

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
      .then(handleSucces)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transition(bodyParams, queryParams)
      .then(handleSucces)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
      .then(handleSucces)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiate(bodyParams, queryParams)
      .then(handleSucces)
      .catch(handleError);
  }
};

export const confirmPayment = (transactionId, transitionName, transitionParams = {},listingId) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(confirmPaymentRequest());

  const bodyParams = {
    id: transactionId,
    transition: transitionName,
    params: transitionParams,
  };

  // Without expand, the transition response often omits `attributes.lineItems`, which breaks
  // post-checkout steps that need line totals (e.g. TEAK order submission).
  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  return sdk.transactions
    .transition(bodyParams, queryParams)
    .then(response => {
      // Raw `response.data.data` omits merged `included` resources (e.g. line items).
      // Match initiateOrder / speculate: denormalise so attributes.lineItems exists for TEAK, etc.
      const entities = denormalisedResponseEntities(response);
      const order = entities[0];
      if (!order?.id) {
        log.error({ response }, 'confirm-payment-missing-transaction');
        throw new Error('Confirm payment response did not include a transaction');
      }
      dispatch(confirmPaymentSuccess(order.id));
      const orderUuid = order.id?.uuid || order.id;
      if (orderUuid) {
        sendGuestBuyerOrderConfirmation({ transactionId: orderUuid }).catch(e => {
          log.error(e, 'guest-buyer-order-confirmation-email-failed', { transactionId: orderUuid });
        });
      }
      // TN event listings represent an *event* (many ticket groups), not a single SKU.
      // Closing them after one sale would hide the rest of the inventory and break
      // subsequent checkouts with `transaction-listing-not-found`. Use the canonical
      // detection (includes truthy `ticketNetworkEventId`) so ingestion variants that
      // only set the TN event id are also protected.
      const listing = getState().CheckoutPage?.listing;
      const pd = listing?.attributes?.publicData || {};
      // Fail-safe: if Redux listing is missing (refresh / session restore), do not
      // close. Closing a TN event listing hides the rest of TN inventory.
      if (listing && !isTicketNetworkEventListing(pd)) {
        closeListingAfterSold({ listingId }).catch(() => {
          // Avoid unhandled rejection (e.g. 504 HTML body); checkout flow must continue
        });
      }
      return order;
    })
    
    .catch(e => {
      dispatch(confirmPaymentError(storableError(e)));
      const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
      log.error(e, 'initiate-order-failed', {
        ...transactionIdMaybe,
      });
      throw e;
    });
};

export const sendMessage = params => (dispatch, getState, sdk) => {
  const message = params.message;
  const orderId = params.id;

  if (message) {
    return sdk.messages
      .send({ transactionId: orderId, content: message })
      .then(() => {
        return { orderId, messageSuccess: true };
      })
      .catch(e => {
        log.error(e, 'initial-message-send-failed', { txId: orderId });
        return { orderId, messageSuccess: false };
      });
  } else {
    return Promise.resolve({ orderId, messageSuccess: true });
  }
};

/**
 * Initiate or transition the speculative transaction with the given
 * booking details
 *
 * The API allows us to do speculative transaction initiation and
 * transitions. This way we can create a test transaction and get the
 * actual pricing information as if the transaction had been started,
 * without affecting the actual data.
 *
 * We store this speculative transaction in the page store and use the
 * pricing info for the booking breakdown to get a proper estimate for
 * the price with the chosen information.
 */
export const speculateTransaction = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => (dispatch, getState, sdk) => {
  dispatch(speculateTransactionRequest());

  // If we already have a transaction ID, we should transition, not
  // initiate.
  const isTransition = !!transactionId;

  const {
    deliveryMethod,
    quantity,
    stockReservationQuantity,
    bookingDates,
    teakPremiumSubUnits,
    rewardsRedeemSubUnits,
    tnSelection,
    ...otherOrderParams
  } = orderParams;
  // Resolve stock qty from every checkout alias. Staging rewards initiates were arriving
  // with rewards/TEAK/paymentMethod but no quantity — recover from protectedData.tnSelection too.
  const resolvedQuantity = (() => {
    const raw =
      quantity != null
        ? quantity
        : stockReservationQuantity != null
        ? stockReservationQuantity
        : tnSelection?.quantity != null
        ? tnSelection.quantity
        : otherOrderParams?.protectedData?.tnSelection?.quantity;
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
      return Math.round(raw);
    }
    if (raw != null && typeof raw === 'object' && typeof raw.toNumber === 'function') {
      const n = raw.toNumber();
      return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
    }
    const n = typeof raw === 'number' ? raw : Number.parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  const quantityMaybe = resolvedQuantity ? { stockReservationQuantity: resolvedQuantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  // stockReservationQuantity on orderData too — lineItems.js reads the merged object.
  // tnSelection stays on orderData only (never forward to Sharetribe transition params).
  const orderData = {
    ...(deliveryMethod ? { deliveryMethod } : {}),
    ...(teakPremiumSubUnits != null ? { teakPremiumSubUnits } : {}),
    ...(rewardsRedeemSubUnits != null ? { rewardsRedeemSubUnits } : {}),
    ...quantityMaybe,
    ...(tnSelection ? { tnSelection } : {}),
  };

  // Parameters for Marketplace API
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
    cardToken: 'CheckoutPage_speculative_card_token',
  };

  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition: transitionName,
        params: transitionParams,
      }
    : {
        processAlias,
        transition: transitionName,
        params: transitionParams,
      };

  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the speculate response');
    }
    const tx = entities[0];
    dispatch(speculateTransactionSuccess(tx));
  };

  const handleError = e => {
    log.error(e, 'speculate-transaction-failed', {
      listingId: transitionParams.listingId.uuid,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
    });
    return dispatch(speculateTransactionError(storableError(e)));
  };

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transitionSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiateSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  }
};

// StripeCustomer is a relantionship to currentUser
// We need to fetch currentUser with correct params to include relationship
export const stripeCustomer = () => (dispatch, getState, sdk) => {
  dispatch(stripeCustomerRequest());

  return dispatch(fetchCurrentUser({ include: ['stripeCustomer.defaultPaymentMethod'] }))
    .then(response => {
      dispatch(stripeCustomerSuccess());
    })
    .catch(e => {
      dispatch(stripeCustomerError(storableError(e)));
    });
};
